// @docs:
//   - docs/core/operations/ROUTER_PORTAL.md  § The landing page
//
// Admin › Departure planner › Test fleet requests: who asked to join from
// router.vortexnav.com's landing page ("Join the test fleet"), and "Invite", which
// emails them how to sign in (vortex-api routes/adminRouterTesters.ts).

import { useCallback, useEffect, useState } from 'react';
import { Card, Pill } from './routingUi';
import { formatLocal } from './weatherFormat';
import { api, type RouterTesterRequest } from '../../services/api';

const smallButton = 'px-3 py-1.5 rounded-lg border border-slate-600 text-[13px] text-slate-200 hover:bg-slate-700 font-semibold disabled:opacity-50';

const errorText = (e: unknown) => {
  const data = (e as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  return data?.error ?? data?.message ?? (e as Error).message;
};

export function RouterTesterRequests() {
  const [rows, setRows] = useState<RouterTesterRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.adminRouterTesterRequests());
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const invite = async (r: RouterTesterRequest) => {
    setBusy(r.id);
    setError(null);
    setNotice(null);
    try {
      const done = await api.adminRouterTesterInvite(r.id);
      setRows((list) => list?.map((x) => (x.id === r.id ? done : x)) ?? null);
      setNotice(`Invitation sent to ${r.email}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title="Test fleet requests" caption="From router.vortexnav.com. Invite emails them how to sign in with an email code.">
      {error && <p className="px-5 pt-4 text-[14px] text-red-300">{error}</p>}
      {notice && <p className="px-5 pt-4 text-[14px] text-slate-300">{notice}</p>}
      {rows && rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left text-slate-400 text-[13px]">
                <th className="px-5 py-2">Asked</th>
                <th className="px-5 py-2">Skipper</th>
                <th className="px-5 py-2">Boat</th>
                <th className="px-5 py-2">Passage</th>
                <th className="px-5 py-2">Dates</th>
                <th className="px-5 py-2">Heard of us</th>
                <th className="px-5 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-700 align-top">
                  <td className="px-5 py-2 text-slate-300 whitespace-nowrap">{formatLocal(Date.parse(r.createdAt))}</td>
                  <td className="px-5 py-2">
                    <p className="text-white font-semibold">{r.name}</p>
                    <p className="text-slate-300">{r.email}</p>
                  </td>
                  <td className="px-5 py-2 text-slate-200">{`${r.boatMake} ${r.boatModel}, ${r.boatLengthM} m`}</td>
                  <td className="px-5 py-2 text-slate-200">{r.passage}</td>
                  <td className="px-5 py-2 text-slate-200">{r.dates}</td>
                  <td className="px-5 py-2 text-slate-300">{r.heardFrom ?? '—'}</td>
                  <td className="px-5 py-2 whitespace-nowrap">
                    {r.status === 'invited' ? (
                      <div>
                        <Pill tone="ok">invited</Pill>
                        {r.invitedAt && <p className="text-[13px] text-slate-400 mt-1">{formatLocal(Date.parse(r.invitedAt))}</p>}
                      </div>
                    ) : (
                      <button type="button" className={smallButton} disabled={busy === r.id} onClick={() => invite(r)}>Invite</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        rows && <p className="px-5 py-4 text-[14px] text-slate-400">No requests yet. They come from “Join the test fleet” on router.vortexnav.com.</p>
      )}
    </Card>
  );
}
